import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/UI";
import Pagination from "@/components/app/Pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, FileText, ChevronRight, ClipboardList, CheckCircle2, AlertCircle, UserCog } from "lucide-react";
import { toast } from "sonner";
import EmployeePicker from "@/components/app/EmployeePicker";

const PHASE_META = {
  initial: { label: "Initial Documentation", chip: "bg-[#FEF3C7] text-[#92400E]", icon: ClipboardList },
  final:   { label: "Final Documentation",   chip: "bg-[#DBEAFE] text-[#1E40AF]", icon: CheckCircle2 },
};

export default function DocumentationTracking() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [phaseTab, setPhaseTab] = useState("all"); // all | initial | final
  const [pmFilter, setPmFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [busyKeys, setBusyKeys] = useState(new Set());
  // Server-side pagination — /documentation-tracking?paged=1 returns
  // {items,total,stats,pms,assignees}. Stats & dropdown options cover the full
  // set; only the current page crosses the wire.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, initial: 0, final: 0, unassigned: 0 });
  const [pms, setPms] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        paged: "1", limit: String(pageSize), skip: String((page - 1) * pageSize),
      });
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (phaseTab !== "all") params.set("phase", phaseTab);
      if (pmFilter !== "all") params.set("pm", pmFilter);
      if (assigneeFilter !== "all") params.set("assignee", assigneeFilter);
      const [{ data }, { data: usersData }] = await Promise.all([
        api.get(`/documentation-tracking?${params}`),
        api.get("/users"),
      ]);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, initial: 0, final: 0, unassigned: 0 });
      setPms(data.pms || []);
      setAssignees(data.assignees || []);
      setUsers(usersData || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [page, pageSize, debouncedQ, phaseTab, pmFilter, assigneeFilter]);
  useEffect(() => { setPage(1); }, [debouncedQ, phaseTab, pmFilter, assigneeFilter, pageSize]);

  const toggleActivity = async (project, activity, nextDone) => {
    const k = `${project.id}:${activity.stage_key}:${activity.sub_key}`;
    setBusyKeys((s) => new Set(s).add(k));
    // optimistic update
    setRows((prev) => prev.map((r) => {
      if (r.id !== project.id) return r;
      return { ...r, pending_activities: r.pending_activities.filter((a) => a.sub_key !== activity.sub_key) };
    }));
    try {
      await api.patch(`/projects/${project.id}/stages-v2`, {
        stage_key: activity.stage_key,
        sub_key: activity.sub_key,
        done: nextDone,
      });
      toast.success(`✓ ${activity.sub_label}`);
      // soft reload to refresh phase/pending list (an activity in compliance may flip the row to "Final" etc.)
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      load(); // resync on failure
    } finally {
      setBusyKeys((s) => { const next = new Set(s); next.delete(k); return next; });
    }
  };

  const setAssignee = async (project, userId) => {
    const k = `assign:${project.id}`;
    setBusyKeys((s) => new Set(s).add(k));
    try {
      const { data } = await api.patch(`/documentation-tracking/${project.id}/assignee`, {
        assignee_id: userId || null,
      });
      setRows((prev) => prev.map((r) => r.id === project.id ? {
        ...r, doc_assignee_id: data.doc_assignee_id, doc_assignee_name: data.doc_assignee_name,
      } : r));
      toast.success(userId ? `Assigned to ${data.doc_assignee_name}` : "Assignee cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally {
      setBusyKeys((s) => { const next = new Set(s); next.delete(k); return next; });
    }
  };

  return (
    <div className="px-6 lg:px-8 py-6 lg:py-10 space-y-6">
      <PageHeader
        eyebrow="Document Operations"
        title="Documentation Tracking"
        subtitle="Projects flowing through the documentation team. Initial tracks Stages 01, 2.2, 03, 04, 08 · Final tracks Stage 10 onwards (10, 11.2, 13.1, 13.3)."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="doc-tracking-stats">
        <StatCard label="Total in Doc Queue" value={stats.total} accent="#002FA7" />
        <StatCard label="Initial Documentation" value={stats.initial} accent="#F59E0B" />
        <StatCard label="Final Documentation" value={stats.final} accent="#1E40AF" />
        <StatCard label="Unassigned" value={stats.unassigned} accent="#EF4444" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            placeholder="Search by project, customer, phone, address, PM, assignee…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-none"
            data-testid="doc-tracking-search"
          />
        </div>

        <Tabs value={phaseTab} onValueChange={setPhaseTab}>
          <TabsList className="rounded-none">
            <TabsTrigger value="all" className="rounded-none uppercase tracking-widest text-[11px] font-bold" data-testid="doc-tab-all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="initial" className="rounded-none uppercase tracking-widest text-[11px] font-bold" data-testid="doc-tab-initial">Initial ({stats.initial})</TabsTrigger>
            <TabsTrigger value="final" className="rounded-none uppercase tracking-widest text-[11px] font-bold" data-testid="doc-tab-final">Final ({stats.final})</TabsTrigger>
          </TabsList>
        </Tabs>

        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="h-10 w-[180px] rounded-none" data-testid="doc-tracking-pm-filter">
            <SelectValue placeholder="All PMs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Project Managers</SelectItem>
            {pms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="h-10 w-[200px] rounded-none" data-testid="doc-tracking-assignee-filter">
            <SelectValue placeholder="All Doc Assignees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Doc Assignees</SelectItem>
            <SelectItem value="unassigned">— Unassigned —</SelectItem>
            {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="ml-auto text-xs text-[#4B5563]" data-testid="doc-tracking-count">
          Showing {rows.length} of {total}
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <div className="grid grid-cols-[1.4fr_0.9fr_1.2fr_1.1fr_1.6fr_60px] gap-3 px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <Col>Project / Customer</Col>
          <Col>Phase</Col>
          <Col>Doc Assignee</Col>
          <Col>Current Stage</Col>
          <Col>Pending Activities (click to complete)</Col>
          <Col />
        </div>

        {loading ? (
          <Empty>Loading…</Empty>
        ) : rows.length === 0 ? (
          <Empty>
            <AlertCircle className="w-5 h-5 mb-2 text-[#9CA3AF]" />
            No projects in this view. Projects appear here once Token Received (1.1) is ticked on the project.
          </Empty>
        ) : (
          rows.map((r) => {
            const meta = PHASE_META[r.doc_phase] || PHASE_META.initial;
            const Icon = meta.icon;
            const assignBusy = busyKeys.has(`assign:${r.id}`);
            const ownershipHold = !!r.ownership_change_needed;
            return (
              <div
                key={`${r.id}-${r.doc_phase}`}
                className={`grid grid-cols-[1.4fr_0.9fr_1.2fr_1.1fr_1.6fr_60px] gap-3 px-4 py-4 border-b border-[#F3F4F6] hover:bg-[#FAFAFA] items-start ${ownershipHold ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]" : ""}`}
                data-testid={`doc-tracking-row-${r.id}`}
              >
                <div className="cursor-pointer" onClick={() => nav(`/projects/${r.id}`)}>
                  <div className="font-semibold text-[#0a0a0a] flex items-center gap-2 flex-wrap">
                    {r.customer_name || r.name}
                    {ownershipHold && (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B]" data-testid={`doc-ownership-badge-${r.id}`}>
                        Ownership Change — On Hold
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#4B5563] mt-0.5">
                    {r.customer_phone || "—"}{r.project_manager ? <> · PM: {r.project_manager}</> : null}
                  </div>
                  {r.address && <div className="text-[11px] text-[#9CA3AF] mt-1 truncate">{r.address}</div>}
                  {r.capacity_kw && <div className="text-[11px] text-[#4B5563] mt-1">{r.capacity_kw} kW</div>}
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${meta.chip}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <div className="text-[10px] text-[#9CA3AF] mt-1.5">
                    {r.doc_phase === "initial"
                      ? <>Token received · {fmtDate(r.token_received_at) || "—"}</>
                      : <>Install done · {fmtDate(r.installation_completed_at) || "—"}</>}
                  </div>
                </div>
                <div>
                  <EmployeePicker
                    employees={users}
                    value={r.doc_assignee_id}
                    onChange={(id) => setAssignee(r, id)}
                    placeholder="— Assign —"
                    disabled={assignBusy}
                    testid={`doc-assignee-${r.id}`}
                  />
                  {r.doc_assignee_name && (
                    <div className="text-[10px] text-[#4B5563] mt-1 flex items-center gap-1">
                      <UserCog className="w-3 h-3" /> {r.doc_assignee_name}
                    </div>
                  )}
                </div>
                <div className="text-sm">
                  {r.current_stage_label}
                  <div className="text-[11px] text-[#4B5563] mt-1">{r.completion_pct}% complete</div>
                </div>
                <div className="space-y-1.5">
                  {(r.pending_activities || []).length === 0 ? (
                    <span className="text-[12px] text-[#10B981]">All docs in this phase done</span>
                  ) : (
                    (r.pending_activities || []).map((pa) => {
                      const k = `${r.id}:${pa.stage_key}:${pa.sub_key}`;
                      const busy = busyKeys.has(k);
                      return (
                        <label
                          key={pa.sub_key}
                          className={`flex items-start gap-2 text-[12px] cursor-pointer group ${busy ? "opacity-50" : ""}`}
                          data-testid={`doc-activity-${r.id}-${pa.sub_key}`}
                        >
                          <Checkbox
                            checked={false}
                            disabled={busy}
                            onCheckedChange={(v) => v && toggleActivity(r, pa, true)}
                            className="mt-0.5"
                          />
                          <span className="leading-tight">
                            <span className="group-hover:text-[#0a0a0a] text-[#4B5563]">{pa.sub_label}</span>
                            <span className="block text-[10px] text-[#9CA3AF]">{pa.stage_label}</span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="flex justify-end pr-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none h-7 px-2"
                    onClick={() => nav(`/projects/${r.id}`)}
                    data-testid={`doc-tracking-open-${r.id}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix="doc-tracking-pagination"
      />

      <div className="text-[11px] text-[#9CA3AF] flex items-center gap-2">
        <FileText className="w-3 h-3" />
        Initial entry: Token Received (1.1) ticked · Final entry: Installation completed. Initial tracks Order Confirmation, Feasibility (2.2), Loan Processing, Approvals & Demand Payment, Compliance & Documentation. Final tracks Document Completion, KSEB Completion Submitted (11.2), Subsidy Redemption (13.1) and Subsidy Disbursal (13.3).
      </div>
    </div>
  );
}

const Col = ({ children, className = "" }) => (
  <div className={`text-[10px] font-bold uppercase tracking-widest text-[#6B7280] ${className}`}>{children}</div>
);

const Empty = ({ children }) => (
  <div className="px-4 py-12 text-center text-sm text-[#6B7280] flex flex-col items-center" data-testid="doc-tracking-empty">{children}</div>
);
